import { dist2, isqrt } from '../lib/intmath';
import { unitDef } from '../content/units';
import { buildingDef } from '../content/buildings';
import {
    ARMOR_BY_NAME,
    PROJECTILE_BY_NAME,
    WARHEAD_BY_NAME,
    WEAPON_BY_NAME,
    damageAgainst,
    projectileDef,
    warheadDef,
    weaponDef,
} from '../content/combat';
import { EventKind, UnitMode } from './types';
import type { Projectile, SimEvent, UnitId } from './types';

/*
 * קרב.
 *
 * שלושה שלבים, בסדר קבוע: **רכישת מטרה → ירי → תנועת קליעים ופגיעות.**
 * הסדר חשוב: יחידה שרוכשת מטרה בטיק הזה יורה בו, וקליע שנורה בטיק הזה מתחיל לנוע
 * רק בטיק הבא — אחרת קליע מהיר היה פוגע באותו טיק שבו נורה והטווח האפקטיבי היה
 * גדול מהמוצהר.
 *
 * שבירת שוויון בבחירת מטרה היא לפי מזהה, לא לפי סדר במערך: מערכים משתנים כשיחידות
 * מתות, ובחירה תלוית-סדר הייתה מייצרת desync בין שני מחשבים.
 */

export interface CombatUnit {
    id: UnitId;
    owner: number;
    type: number;
    x: number;
    y: number;
    hp: number;
    mode: UnitMode;
    goalTile: number;
    path: number[];
    homeX: number;
    homeY: number;
    targetId: UnitId;
    cooldown: number;
    burstLeft: number;
    cargo: number;
    /**
     * סוג מבנה, או -1 ליחידה.
     *
     * מבנים משתתפים בקרב דרך אותה מערכת בדיוק: הם רוכשים מטרה, יורים וסופגים נזק.
     * בלי זה מגדל הגנה היה קישוט, ואי אפשר היה להרוס בסיס — כלומר גם אי אפשר
     * היה לנצח.
     */
    buildingType: number;
}

/** רדיוס פגיעה: מהיחידה, או מחצי האלכסון של תפיסת המבנה. */
export function hitRadius(entity: CombatUnit): number {
    if (entity.buildingType < 0) return unitDef(entity.type).radius;
    const def = buildingDef(entity.buildingType);
    return (Math.max(def.w, def.h) * 256) / 2;
}

/** האם היחידה יכולה לירות בכלל. */
export function weaponOf(entity: CombatUnit): number | null {
    const name =
        entity.buildingType < 0
            ? unitDef(entity.type).weapon
            : buildingDef(entity.buildingType).weapon;
    return name === null ? null : WEAPON_BY_NAME[name];
}

function armorOf(entity: CombatUnit): number {
    const name =
        entity.buildingType < 0
            ? unitDef(entity.type).armor
            : buildingDef(entity.buildingType).armor;
    return ARMOR_BY_NAME[name];
}

/**
 * בוחרת מטרה: האויב הקרוב ביותר בטווח הרלוונטי לעמדה.
 * שוויון מרחק נשבר לפי מזהה, כדי שהבחירה תהיה זהה בכל מחשב.
 */
/**
 * באיזו שכבה נמצאת ישות קרב. **מבנה הוא תמיד קרקע** — הוא נכנס למערכת דרך מתאם
 * שנראה כמו יחידה, ומתאם בלי שכבה היה הופך כל מבנה למטרה חוקית לנשק נ"מ.
 */
export function layerOf(unit: CombatUnit): 'ground' | 'air' {
    return unit.buildingType < 0 ? unitDef(unit.type).layer : 'ground';
}

/**
 * האם הנשק הזה יכול בכלל לירות במטרה הזו.
 *
 * **זה הכלל היחיד שמפריד בין אוויר לקרקע.** הוא נשאל גם ברכישת מטרה מיוזמה וגם
 * בפקודת תקיפה מפורשת — אחרת השחקן היה יכול לצוות לטנק לתקוף מסוק, והטנק היה
 * רודף אותו לנצח בלי לירות אף פעם.
 */
export function canTarget(weaponId: number, target: CombatUnit): boolean {
    return weaponDef(weaponId).targets.includes(layerOf(target));
}

export function acquireTarget(
    unit: CombatUnit,
    units: readonly CombatUnit[],
    weaponId: number,
): UnitId {
    const weapon = weaponDef(weaponId);
    const sight =
        unit.buildingType < 0
            ? unitDef(unit.type).sightRange
            : buildingDef(unit.buildingType).sightRange;

    // ב"עצור" לא מחפשים רחוק: רק מה שנכנס לטווח הנשק עצמו.
    const reach = unit.mode === UnitMode.Holding ? weapon.range : Math.max(weapon.range, sight);
    const reach2 = reach * reach;

    let best: UnitId = -1;
    let bestDistance = Number.MAX_SAFE_INTEGER;

    for (const other of units) {
        if (other.owner === unit.owner || other.hp <= 0) continue;
        if (!canTarget(weaponId, other)) continue;
        const d2 = dist2(unit.x, unit.y, other.x, other.y);
        if (d2 > reach2) continue;
        if (d2 < bestDistance || (d2 === bestDistance && other.id < best)) {
            bestDistance = d2;
            best = other.id;
        }
    }

    return best;
}

export function inWeaponRange(unit: CombatUnit, target: CombatUnit, weaponId: number): boolean {
    const range = weaponDef(weaponId).range;
    return dist2(unit.x, unit.y, target.x, target.y) <= range * range;
}

export interface FireResult {
    readonly projectile: Projectile | null;
    readonly instantDamage: number;
}

/**
 * מבצעת ירייה אחת. מחזירה קליע מעופף, או נזק מיידי לנשק ללא קליע.
 * מעדכנת את הצינון ואת מונה המטח על היחידה היורה.
 */
export function fire(
    shooter: CombatUnit,
    target: CombatUnit,
    weaponId: number,
    nextEntityId: number,
    events: SimEvent[],
): FireResult {
    const weapon = weaponDef(weaponId);
    const warheadId = WARHEAD_BY_NAME[weapon.warhead];

    // מטח: היריות בתוך המטח צמודות, וההמתנה המלאה מגיעה בסופו.
    if (shooter.burstLeft <= 0) shooter.burstLeft = weapon.burst;
    shooter.burstLeft--;
    shooter.cooldown = shooter.burstLeft > 0 ? 2 : weapon.rofTicks;

    events.push({
        kind: EventKind.Shot,
        x: shooter.x,
        y: shooter.y,
        a: target.x,
        b: target.y,
    });

    if (weapon.projectile === null) {
        return {
            projectile: null,
            instantDamage: damageAgainst(warheadId, armorOf(target), weapon.damage),
        };
    }

    return {
        projectile: {
            id: nextEntityId,
            owner: shooter.owner,
            type: PROJECTILE_BY_NAME[weapon.projectile],
            x: shooter.x,
            y: shooter.y,
            targetId: target.id,
            lastX: target.x,
            lastY: target.y,
            damage: weapon.damage,
            warhead: warheadId,
        },
        instantDamage: 0,
    };
}

export interface ProjectileStep {
    readonly survivors: Projectile[];
    readonly impacts: { x: number; y: number; damage: number; warhead: number }[];
}

/**
 * מקדמת את הקליעים. קליע עוקב אחרי המטרה החיה שלו; אם היא מתה הוא ממשיך אל
 * הנקודה האחרונה שבה נראתה ומתפוגג שם — פגז שכבר עזב את הקנה לא נעלם באוויר.
 */
export function advanceProjectiles(
    projectiles: readonly Projectile[],
    byId: ReadonlyMap<UnitId, CombatUnit>,
    events: SimEvent[],
): ProjectileStep {
    const survivors: Projectile[] = [];
    const impacts: { x: number; y: number; damage: number; warhead: number }[] = [];

    for (const projectile of projectiles) {
        const target = byId.get(projectile.targetId);
        const alive = target !== undefined && target.hp > 0;
        const aimX = alive ? target.x : projectile.lastX;
        const aimY = alive ? target.y : projectile.lastY;

        const speed = projectileDef(projectile.type).speed;
        const dx = aimX - projectile.x;
        const dy = aimY - projectile.y;
        const distance = isqrt(dx * dx + dy * dy);

        if (distance <= speed) {
            impacts.push({
                x: aimX,
                y: aimY,
                // קליע שאיבד את מטרתו נופל לריק ואינו מזיק למי שבמקרה עומד שם.
                damage: alive ? projectile.damage : 0,
                warhead: projectile.warhead,
            });
            events.push({
                kind: EventKind.Impact,
                x: aimX,
                y: aimY,
                a: projectile.warhead,
                b: alive ? 1 : 0,
            });
            continue;
        }

        survivors.push({
            ...projectile,
            x: projectile.x + Math.trunc((dx * speed) / distance),
            y: projectile.y + Math.trunc((dy * speed) / distance),
            lastX: aimX,
            lastY: aimY,
        });
    }

    return { survivors, impacts };
}

/**
 * מחילה פגיעה: נזק ישיר למטרה, ונזק היקפי דועך לכל מי שברדיוס.
 * הנזק תמיד עובר דרך מטריצת `warhead × armor` — אין כאן "נזק גולמי".
 */
export function applyImpact(
    impact: { x: number; y: number; damage: number; warhead: number },
    units: readonly CombatUnit[],
): void {
    if (impact.damage <= 0) return;

    const warhead = warheadDef(impact.warhead);
    const splash = warhead.splashRadius;

    for (const unit of units) {
        if (unit.hp <= 0) continue;
        const radius = hitRadius(unit);
        const d2 = dist2(impact.x, impact.y, unit.x, unit.y);

        if (d2 <= radius * radius) {
            unit.hp -= damageAgainst(impact.warhead, armorOf(unit), impact.damage);
            continue;
        }

        if (splash > 0 && d2 <= splash * splash) {
            // דעיכה לינארית עם המרחק — שלמה, בלי חזקות.
            const distance = isqrt(d2);
            const scaled = Math.floor((impact.damage * (splash - distance)) / splash);
            if (scaled > 0) unit.hp -= damageAgainst(impact.warhead, armorOf(unit), scaled);
        }
    }
}
